import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  useAssignUserScopeGrantsMutation,
  useChangeOrganizationUserRoleMutation,
  useLazyGetUserScopeGrantsQuery,
} from "../../../../redux/api/org-user/orgUserAPI";
import { EOrgUserRole } from "../../../../shared/oversight-core/enums/org-user-role";
import { IAccessGrant } from "../../../../shared/oversight-core/interfaces/access-grant";
import { IAccessTarget } from "../../../../shared/oversight-core/interfaces/access-target";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import { IOrgUser } from "../../../../shared/oversight-core/interfaces/org-user";
import AppModal from "../../../../shared/oversight-core/ui-elements/app-modal/app-modal";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import {
  getAllowedAccessLevels,
  getOverlapExample,
  isExpiryRequired,
  orgUserRoleLabels,
} from "../../../../shared/oversight-core/utils/get-org-user-info";
import { toAccessGrant, toRequestedGrant } from "../../users-helpers";
import AddAccessButton from "../add-access-button/add-access-button";
import AddAccessModal from "../add-access-modal/add-access-modal";
import GrantRow from "./grant-row/grant-row";
import styles from "./manage-access-modal.module.scss";

interface IProps {
  show: boolean;
  user?: IOrgUser;
  tenantId: string;
  currentUserId?: string;
  accessTargets: IAccessTarget[];
  isLoadingTargets: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const roleOptions: Option[] = Object.values(EOrgUserRole).map((role) => ({
  value: role,
  label: orgUserRoleLabels[role],
}));

const getErrorMessage = (error: unknown) =>
  (error as IHttpError)?.message || "Sorry, an error occurred.";

const ManageAccessModal = (props: IProps) => {
  const {
    show,
    user,
    tenantId,
    currentUserId,
    accessTargets,
    isLoadingTargets,
    onClose,
    onSaved,
  } = props;
  const [role, setRole] = useState<EOrgUserRole>();
  const [grants, setGrants] = useState<IAccessGrant[]>([]);
  const [showAddAccess, setShowAddAccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [triggerGetGrants, { isFetching: isFetchingGrants, isError }] =
    useLazyGetUserScopeGrantsQuery();
  const [assignGrants] = useAssignUserScopeGrantsMutation();
  const [changeRole] = useChangeOrganizationUserRoleMutation();

  // Start from the user's saved access every time the modal opens, so a
  // cancelled edit never leaks into the next one.
  useEffect(() => {
    let isCurrent = true;

    if (show && user && tenantId) {
      setRole(user.role);
      setGrants([]);
      setShowAddAccess(false);
      setErrorMessage(undefined);
      triggerGetGrants({ tenantId, userId: user.identity }, false)
        .unwrap()
        .then((res) => {
          if (isCurrent) setGrants(res.grants.map((g) => toAccessGrant(g)));
        })
        .catch(() => {
          if (isCurrent) setGrants([]);
        });
    }

    return () => {
      isCurrent = false;
    };
  }, [show, user, tenantId, triggerGetGrants]);

  // The tree may arrive after the grants, so breadcrumbs are joined at render time.
  const targetsById = useMemo(
    () => new Map(accessTargets.map((t) => [t.identity, t])),
    [accessTargets],
  );
  const displayGrants = grants.map((grant) => {
    const target = targetsById.get(grant.identity);
    return target
      ? {
          ...grant,
          parentIdentity: target.parentIdentity,
          path: target.path,
          ancestorIds: target.ancestorIds,
          isRetired: target.isRetired,
        }
      : grant;
  });

  const allowedLevels = getAllowedAccessLevels(role);
  const isOwnAccount = !!user && user.identity === currentUserId;
  const overlapExample = getOverlapExample(displayGrants);
  const hasMissingExpiry = grants.some(
    (g) => !g.isMissing && isExpiryRequired(g.level) && !g.expiresAt,
  );
  const hasLevelNotAllowed = grants.some(
    (g) => !g.isMissing && !allowedLevels.includes(g.level),
  );

  const getFooterMessage = () => {
    if (errorMessage) return errorMessage;
    if (hasLevelNotAllowed)
      return "A Monitoring guest can only hold View redacted grants.";
    if (hasMissingExpiry) return "View redacted grants need an expiry date.";
    return undefined;
  };
  const footerMessage = getFooterMessage();

  const updateGrant = (grant: IAccessGrant) =>
    setGrants((ps) =>
      ps.map((g) => (g.identity === grant.identity ? grant : g)),
    );

  const removeGrant = (identity: string) =>
    setGrants((ps) => ps.filter((g) => g.identity !== identity));

  // Saving replaces the whole list. When the role changes too, the order matters: a move to
  // Monitoring guest needs the grants narrowed first; any other change sets the role first.
  // The two calls aren't atomic, so a failed second call says the first went through.
  const save = async () => {
    if (!user || !role) return;

    const putGrants = () =>
      assignGrants({
        tenantId,
        userId: user.identity,
        grants: grants.map(toRequestedGrant),
      }).unwrap();
    const patchRole = () =>
      changeRole({ tenantId, userId: user.identity, role }).unwrap();

    const steps =
      role === user.role
        ? [{ run: putGrants, done: "" }]
        : role === EOrgUserRole.ROLE_ORG_GUEST
          ? [
              { run: putGrants, done: "The access list was saved" },
              { run: patchRole, done: "" },
            ]
          : [
              { run: patchRole, done: "The role was changed" },
              { run: putGrants, done: "" },
            ];

    setIsSaving(true);
    setErrorMessage(undefined);
    let completed = 0;
    try {
      for (const step of steps) {
        await step.run();
        completed++;
      }
      onSaved();
    } catch (error) {
      const firstStep = completed > 0 ? steps[completed - 1].done : "";
      setErrorMessage(
        firstStep
          ? `${firstStep}, but the rest wasn't: ${getErrorMessage(error)}`
          : getErrorMessage(error),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Add access replaces this modal while it is open; Back returns here with edits intact. */}
      <AppModal show={show && !showAddAccess} size="modal-lg">
        <>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="text-dark font-size-24 font-weight-600">
                Manage access
              </div>
              <div className="d-flex align-items-center gap-1 text-light font-size-13 mt-1">
                <MaterialIcon icon="person_outline" size={16} />
                {[user?.name, user?.email ?? user?.mobile]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <MaterialIcon
              icon="close"
              className="cursor-pointer"
              onClick={onClose}
            />
          </div>

          <div className={styles.divider} />

          <div className="text-dark font-size-14 font-weight-500 mb-2">
            Role
          </div>
          <AppSelect
            options={roleOptions}
            selectedValue={roleOptions.find((o) => o.value === role)}
            onChangeOption={(option) => setRole(option.value as EOrgUserRole)}
            disabled={isOwnAccount}
            fontSize="14px"
          />
          <p className="text-light font-size-12 mt-2 mb-0">
            {isOwnAccount
              ? "You can't change your own role."
              : "The role says what kind of user this is. Access below says where they can work and how much they can do there."}
          </p>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="font-size-14">
              <span className="text-dark font-weight-500">Access</span>
              <span className="text-light">
                {" "}
                · {grants.length} grant{grants.length === 1 ? "" : "s"}
              </span>
            </div>
            <AddAccessButton onClick={() => setShowAddAccess(true)} />
          </div>

          <div className="d-flex flex-column gap-3 mt-4">
            {isFetchingGrants ? (
              <div className="container-dash text-center text-light font-size-12">
                Loading access...
              </div>
            ) : isError ? (
              <div className="container-dash text-center text-danger font-size-12">
                This user's access couldn't be loaded. Close and try again.
              </div>
            ) : displayGrants.length > 0 ? (
              displayGrants.map((grant) => (
                <GrantRow
                  key={grant.identity}
                  grant={grant}
                  allowedLevels={allowedLevels}
                  onChange={updateGrant}
                  onRemove={() => removeGrant(grant.identity)}
                />
              ))
            ) : (
              <div className="container-dash text-center text-light font-size-12">
                This user sees nothing until access is added.
              </div>
            )}
          </div>

          <div
            className={`${styles.infoBox} d-flex gap-2 text-light font-size-12 mt-4`}
          >
            <MaterialIcon icon="info" size={18} color="#2f2a89" />
            <span>
              Access flows down: a grant on a branch covers every site under it.
              Where two grants overlap, the closer one wins
              {overlapExample ? `, so ${overlapExample}.` : "."}
            </span>
          </div>

          <div className={styles.divider} />

          <Row className="align-items-center g-3">
            <Col className="col-auto">
              <AppButton
                text="Save access"
                size="medium"
                isLoading={isSaving}
                disabled={
                  hasMissingExpiry ||
                  hasLevelNotAllowed ||
                  !role ||
                  isFetchingGrants ||
                  isError
                }
                onClick={save}
              />
            </Col>
            <Col className="col-auto">
              <AppButton
                text="Cancel"
                variant="transparentWithBorder"
                size="medium"
                onClick={onClose}
              />
            </Col>
            <Col
              className={`font-size-12 ${
                footerMessage ? "text-danger" : "text-light"
              }`}
            >
              {footerMessage ??
                "Saving replaces this user's whole access list."}
            </Col>
          </Row>
        </>
      </AppModal>
      <AddAccessModal
        show={show && showAddAccess}
        userName={user?.name ?? ""}
        existingGrants={grants}
        accessTargets={accessTargets}
        isLoadingTargets={isLoadingTargets}
        allowedLevels={allowedLevels}
        onBack={() => setShowAddAccess(false)}
        onSave={(grant) => {
          setGrants((ps) => [...ps, grant]);
          setShowAddAccess(false);
        }}
      />
    </>
  );
};

export default ManageAccessModal;
